import * as React from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import ReactInlineSvg from 'react-inlinesvg';
import './Main.scss';

import { idStrongboxPortalHeaderWindow } from '../Utils/Constants';

import {
    Button,
    Col,
    Container,
    Row,
} from 'reactstrap';

import { ApplicationState } from '../Store';
import {
    GetStrongboxHelpCenterLink,
} from '../Store/AppSettings';
import {
    GetPortalLoginText,
    GetPortalText,
} from '../Store/PortalAssets';
import {
    GetDataCollectionLimits,
    GetTenantName,
} from '../Store/Tenant';
import { UserCanNavigateHome, } from '../Store/UIState';
import { GetAuth0Id } from '../Store/User';
import { pathConstants } from '../Utils/Constants';
import {
    DataCollectionLimitSummary,
} from './DataCollectionLimitSummary/DataCollectionLimitSummary';
import { LoggedInUser } from './LoggedInUser/LoggedInUser';

import { GetTenantBannerPath } from '../Utils/BrandUtils';
import { PortalFlow } from '../Utils/PortalTypes';
import { AppTitleHeader } from './Utils/AppTitleHeader';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    PortalSafeDataCollectionLimitResponse,
    PortalTextCommon,
    PortalTextLogin,
} from '../Models/Api/strongbox.financialportal';

import {
    faQuestion,
} from '@awesome.me/kit-8a0d2fd588/icons/modules/sharp/light';
import { faCircleQuestion } from '@awesome.me/kit-8a0d2fd588/icons/modules/classic/light';

import AppLogo from '../images/applogo.svg';
import './PortalHeader.scss';


type InjectedReduxState = {
    tenantName: string;
    auth0Id: string;
    canNavigateHome: boolean;
    commonText?: PortalTextCommon;
    portalLoginText?: PortalTextLogin;
    dataCollectionLimits?: PortalSafeDataCollectionLimitResponse;
    helperCenterLink?: string;
};

type InjectedActionCreators = {};

type PortalHeaderProps = {
    hideMenu?: boolean;
    hideDataCollectionsRemaining?: boolean;
    logout?: () => void;
    settings?: () => void;
    dataCollectionReport?: () => void;
    notificationSettings?: () => void;
    showLoggedInUser?: boolean;
    showHelp?: boolean;
    flow: PortalFlow;
    containingStyle?: any;
    blockNavigateHome?: boolean;
    onPortalLanguageChange?: (languageCode: string) => void;
    centeredLogoOnly?: boolean;
};

type Props = InjectedReduxState & InjectedActionCreators & PortalHeaderProps;

export const PortalHeaderComponent: React.FC<Props> = (props): React.ReactElement => {
    const {
        canNavigateHome,
        flow,
        hideMenu,
        hideDataCollectionsRemaining,
        logout,
        settings,
        dataCollectionReport,
        notificationSettings,
        showLoggedInUser,
        tenantName,
        onPortalLanguageChange,
        commonText,
        dataCollectionLimits,
        centeredLogoOnly,
        showHelp,
        helperCenterLink,
        portalLoginText,
    } = props;

    const rightDivClass = `portal-header-region control-region-lender`;

    const navigate = useNavigate();

    const [bannerPath, setUseBannerPath] = React.useState<string | undefined>(undefined);

    const helpButtonContent = React.useMemo(() => {
        return (
            <a target={'_blank'} rel={'noopener noreferrer'} href={helperCenterLink}>
                <FontAwesomeIcon icon={faQuestion} />
                <span>
                    {commonText?.helpPrompt || ''}
                </span>
            </a>
        )

        // nothing needed in dependencies beyond commonText, just want this to run once on initial load.
        // eslint-disable-next-line react-hooks/exhaustive-deps 
    }, [commonText]);

    React.useEffect(() => {
        GetTenantBannerPath(tenantName)
            .then((path) => {
                setUseBannerPath(path);
            });

        // nothing needed in dependencies want this to run on initial load.
        // eslint-disable-next-line react-hooks/exhaustive-deps 
    }, []);

    if ((flow === 'lender') && (centeredLogoOnly !== true)) {
        return (
            <header className={'page-app-header'} id={idStrongboxPortalHeaderWindow}>
                <img
                    className={'page-app-header__brand'}
                    src={AppLogo}
                    alt={portalLoginText?.appTitle || ''}
                />
                <div className={'page-app-header__actions'}>
                    {(hideDataCollectionsRemaining !== true) && !!dataCollectionLimits && dataCollectionLimits.hasLimit && (
                        <DataCollectionLimitSummary />
                    )}
                    {(showHelp === true) && (!!commonText) && (
                        <a
                            className={'cr-btn cr-btn--floating'}
                            target={'_blank'}
                            rel={'noopener noreferrer'}
                            href={helperCenterLink}
                        >
                            <span className={'cr-btn__icon'}><FontAwesomeIcon icon={faCircleQuestion} /></span>
                            <span className={'cr-btn__label'}>{commonText?.helpPrompt || ''}</span>
                        </a>
                    )}
                    {(showLoggedInUser === true) && (
                        <LoggedInUser
                            logout={logout}
                            settings={settings}
                            dataCollectionReport={dataCollectionReport}
                            notificationSettings={notificationSettings}
                            onPortalLanguageChange={onPortalLanguageChange}
                            hideMenu={hideMenu}
                        />
                    )}
                </div>
            </header>
        );
    }

    return (
        <header>
            <Container
                fluid className={`control-region banner-div ${(flow === 'borrower' || centeredLogoOnly === true) ? 'banner-div-borrower' : 'banner-div-lender'}`}
                id={idStrongboxPortalHeaderWindow}
            >
                <Row className={(flow === 'borrower' || centeredLogoOnly === true) ? 'spaced-row-centered banner-row-borrower' : 'spaced-row banner-row'}>
                    <Col
                        xs={(flow === 'borrower') ? 12 : 4}
                        l={(flow === 'borrower') ? 12 : 6}
                        className={`full-height-column`}
                    >
                        {centeredLogoOnly === true && !!bannerPath && (
                            <ReactInlineSvg
                                className={`${canNavigateHome ? 'navigable' : ''} top-content banner-image`}
                                src={bannerPath}
                                title={commonText?.portalHome || ''}
                                onClick={() => { canNavigateHome && navigate(pathConstants.home) }}
                                aria-label={tenantName}
                            />
                        )}
                        {
                            centeredLogoOnly !== true && (
                                <AppTitleHeader leftAlign={true} />
                            )
                        }
                        {(flow === 'borrower') && (!!commonText) && (
                            <Button
                                className={'top-level-button end-positioned-control'}
                                color="secondary"
                            >
                                {helpButtonContent}
                            </Button>
                        )}
                    </Col>

                    {(flow === 'lender') && (centeredLogoOnly !== true) && (
                        <Col className={`full-height-column ${rightDivClass}`}>
                            <Row className={'header-right-contents'}>
                                {(hideDataCollectionsRemaining !== true) && !!dataCollectionLimits && dataCollectionLimits.hasLimit && (
                                    <Col className={'header-elements-container'}><DataCollectionLimitSummary /></Col>
                                )}
                                {(showHelp === true) && (!!commonText) && (
                                    <Col className={'header-elements-container'}>
                                        <Button
                                            className={'top-level-button minimizing-button'}
                                            color="secondary"
                                        >
                                            {helpButtonContent}
                                        </Button>
                                    </Col>
                                )}
                                {(showLoggedInUser === true) && (
                                    <Col className={'header-elements-container'}>
                                        <LoggedInUser
                                            logout={logout}
                                            settings={settings}
                                            dataCollectionReport={dataCollectionReport}
                                            notificationSettings={notificationSettings}
                                            onPortalLanguageChange={onPortalLanguageChange}
                                            hideMenu={hideMenu}
                                        />
                                    </Col>
                                )}
                            </Row>
                        </Col>
                    )}
                </Row>
            </Container>
        </header>
    );
}

export const PortalHeader = connect<InjectedReduxState, InjectedActionCreators, PortalHeaderProps, ApplicationState>(
    (appState: ApplicationState, props: PortalHeaderProps) => {
        const result: InjectedReduxState = {
            tenantName: GetTenantName(appState),
            auth0Id: GetAuth0Id(appState),
            canNavigateHome: !!props.blockNavigateHome ? false : UserCanNavigateHome(appState),
            commonText: GetPortalText<PortalTextCommon>(appState, 'commonText'),
            portalLoginText: GetPortalLoginText(appState),
            dataCollectionLimits: GetDataCollectionLimits(appState),
            helperCenterLink: GetStrongboxHelpCenterLink(appState),
        };

        return result;
    }
)(PortalHeaderComponent);

